"use client";

import { useState } from "react";
import {
    createComment, deleteComment, exportComments, reopenComment, resolveComment, type PrototypeComment,
} from "@/lib/prototypes/comments";
import { ANCHORS, SCREEN_LABEL, type ViewId } from "./screens";

function Tag({ children, green }: { children: React.ReactNode; green?: boolean }) {
    return <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${green ? "bg-green-100 text-green-700" : "bg-[#003d45]/10 text-[#003d45]"}`}>{children}</span>;
}

export const PROTOTYPE_KEY = "governance.regular_meetings";

function when(iso: string | null) {
    return iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
}

function CommentItem({ c, meId, onChange }: { c: PrototypeComment; meId?: string; onChange: () => void }) {
    const [resolving, setResolving] = useState(false);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const act = async (fn: () => Promise<unknown>) => {
        setBusy(true); setErr(null);
        try { await fn(); onChange(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };
    return (
        <li className={`rounded-lg border p-2.5 text-xs ${c.resolved_at ? "border-gray-200 bg-gray-50" : "border-[#007a8a]/30 bg-white"}`}>
            <div className="flex flex-wrap items-center gap-1.5">
                <b className="text-[#003d45]">{c.author_name ?? "Former user"}</b>
                <span className="text-[10px] text-gray-400">{when(c.created_at)}</span>
                {c.anchor_label && <Tag>{c.anchor_label}</Tag>}
                {c.resolved_at && <Tag green>✓ Resolved</Tag>}
            </div>
            <p className="mt-1 whitespace-pre-wrap text-gray-800">{c.body}</p>
            {c.resolved_at && (
                <p className="mt-1 text-[11px] text-gray-500">Resolved by {c.resolved_by_name}: {c.resolution_note || "—"}</p>
            )}
            {err && <p className="mt-1 text-red-600">{err}</p>}
            <div className="mt-1.5 flex flex-wrap gap-3 text-[11px] font-semibold">
                {!c.resolved_at && !resolving && <button type="button" disabled={busy} className="text-[#007a8a] hover:underline" onClick={() => setResolving(true)}>Resolve</button>}
                {c.resolved_at && <button type="button" disabled={busy} className="text-[#007a8a] hover:underline" onClick={() => act(() => reopenComment(c.id))}>Reopen</button>}
                {meId && c.author_id === meId && <button type="button" disabled={busy} className="text-red-600 hover:underline" onClick={() => act(() => deleteComment(c.id))}>Delete</button>}
            </div>
            {resolving && (
                <div className="mt-2 space-y-1.5">
                    <input autoFocus value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was decided? (e.g. will change, won't change because…)"
                        className="w-full rounded border border-gray-300 px-2 py-1" />
                    <div className="flex gap-2">
                        <button type="button" disabled={busy} className="rounded bg-[#007a8a] px-2 py-1 text-white" onClick={() => act(() => resolveComment(c.id, note))}>Mark resolved</button>
                        <button type="button" className="text-gray-500" onClick={() => setResolving(false)}>Cancel</button>
                    </div>
                </div>
            )}
        </li>
    );
}

export function CommentsDrawer({ open, onClose, tab, comments, meId, reload, error }: {
    open: boolean; onClose: () => void; tab: ViewId; comments: PrototypeComment[]; meId?: string; reload: () => void; error: string | null;
}) {
    const [body, setBody] = useState("");
    const [anchor, setAnchor] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const mine = comments.filter((c) => c.screen_key === tab);

    const submit = async () => {
        if (!body.trim()) return;
        setBusy(true); setErr(null);
        try {
            await createComment({ prototype_key: PROTOTYPE_KEY, screen_key: tab, anchor_label: anchor || null, body });
            setBody(""); setAnchor("");
            reload();
        } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/20" onClick={onClose}>
            <aside id="gov-comments-drawer" onClick={(e) => e.stopPropagation()} className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
                <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Reviewer comments</p>
                        <h3 className="text-sm font-bold text-[#003d45]">{SCREEN_LABEL[tab]}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700"><span className="material-symbols-outlined">close</span></button>
                </header>
                <div className="space-y-2 border-b border-gray-100 p-4">
                    <select value={anchor} onChange={(e) => setAnchor(e.target.value)} className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs">
                        <option value="">This screen in general</option>
                        {ANCHORS[tab].map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000}
                        placeholder="What would you change, what is missing, what is unclear?" className="w-full rounded-lg border border-gray-300 p-2 text-xs" />
                    {(err || error) && <p className="text-xs text-red-600">{err || error}</p>}
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-400">Comments are saved and seen by the team.</span>
                        <button type="button" disabled={busy || !body.trim()} onClick={submit}
                            className="rounded-lg bg-[#007a8a] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Post comment</button>
                    </div>
                </div>
                <ul className="flex-1 space-y-2 overflow-y-auto p-4">
                    {mine.length === 0 ? <li className="text-center text-xs text-gray-400">No comments on this screen yet.</li>
                        : mine.slice().reverse().map((c) => <CommentItem key={c.id} c={c} meId={meId} onChange={reload} />)}
                </ul>
            </aside>
        </div>
    );
}

export function FeedbackView({ comments, meId, reload, error }: { comments: PrototypeComment[]; meId?: string; reload: () => void; error: string | null }) {
    const [filter, setFilter] = useState<"open" | "resolved" | "all">("open");
    const [exportErr, setExportErr] = useState<string | null>(null);
    const shown = comments.filter((c) => filter === "all" || (filter === "open" ? !c.resolved_at : !!c.resolved_at));
    const screens = Array.from(new Set(shown.map((c) => c.screen_key)));
    const open = comments.filter((c) => !c.resolved_at).length;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div>
                    <h3 className="text-sm font-bold text-[#003d45]">Reviewer comments</h3>
                    <p className="text-xs text-gray-500">{comments.length} comments · {open} open. Mark each one resolved with what was decided, and download them all as a spreadsheet.</p>
                </div>
                <div className="flex items-center gap-2">
                    {(["open", "resolved", "all"] as const).map((f) => (
                        <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${filter === f ? "border-[#003d45] bg-[#003d45] text-white" : "border-gray-300 text-gray-600"}`}>{f}</button>
                    ))}
                    <button type="button" onClick={() => exportComments(PROTOTYPE_KEY).catch((e) => setExportErr((e as Error).message))}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-[#003d45] hover:bg-gray-50">
                        <span className="material-symbols-outlined text-[16px]">download</span>Download
                    </button>
                </div>
            </div>
            {(error || exportErr) && <p className="text-xs text-red-600">{error || exportErr}</p>}
            {screens.length === 0 ? <p className="py-8 text-center text-sm text-gray-400">No {filter === "all" ? "" : filter} comments yet. Use the yellow Comments button on any screen.</p> : screens.map((sc) => (
                <section key={sc}>
                    <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">{SCREEN_LABEL[sc] ?? sc}</h4>
                    <ul className="grid gap-2 md:grid-cols-2">
                        {shown.filter((c) => c.screen_key === sc).map((c) => <CommentItem key={c.id} c={c} meId={meId} onChange={reload} />)}
                    </ul>
                </section>
            ))}
        </div>
    );
}
